import { useEffect, useState } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../store/cart';
import { useRackCatalogue } from './useCatalogue';
import { CartDrawer } from './CartDrawer';
import { PageLoader, ErrorState } from '../components/ui';
import { ApiRequestError } from '../lib/api';

export function VisitorLayout() {
  const { qrToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cartOpen, setCartOpen] = useState(false);
  const setContext = useCart((s) => s.setContext);
  const count = useCart((s) => s.count());

  const { data, isLoading, error, refetch } = useRackCatalogue(qrToken);

  useEffect(() => {
    if (data && qrToken) setContext(qrToken, data.rack.number);
  }, [data, qrToken, setContext]);

  const onProduct = location.pathname.includes('/p/');
  const onCheckout = location.pathname.endsWith('/checkout') || location.pathname.includes('/confirmed/');

  return (
    <div className="mx-auto flex min-h-screen max-w-xl flex-col bg-white">
      <header className="sticky top-0 z-30 border-b border-ink-100 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <button
            className="flex min-w-0 items-center gap-2 text-left"
            onClick={() => qrToken && navigate(`/r/${qrToken}`)}
          >
            {onProduct && (
              <span className="text-ink-400" aria-hidden>
                ‹
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate font-serif text-base font-semibold leading-tight text-ink-900">
                {data?.expo.name ?? 'Jewellery Exhibition'}
              </p>
              <p className="truncate text-xs text-ink-400">
                {data ? `Stall ${data.stall.number} · Rack ${data.rack.number}` : ' '}
              </p>
            </div>
          </button>

          {!onCheckout && (
            <button
              className="relative shrink-0 rounded-full border border-ink-200 p-2 text-ink-700"
              onClick={() => setCartOpen(true)}
              aria-label="Open cart"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 6h15l-1.5 9h-12z" strokeLinejoin="round" />
                <circle cx="9" cy="20" r="1.4" />
                <circle cx="18" cy="20" r="1.4" />
                <path d="M6 6 5 3H3" strokeLinecap="round" />
              </svg>
              {count > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-gold-500 px-1 text-[11px] font-semibold text-white">
                  {count}
                </span>
              )}
            </button>
          )}
        </div>
      </header>

      <main className="flex-1">
        {isLoading ? (
          <PageLoader label="Opening the collection…" />
        ) : error ? (
          <div className="p-6">
            <ErrorState
              message={
                error instanceof ApiRequestError
                  ? error.message
                  : 'This QR code could not be opened.'
              }
              onRetry={() => refetch()}
            />
          </div>
        ) : (
          <Outlet context={{ catalogue: data }} />
        )}
      </main>

      <footer className="border-t border-ink-100 px-4 py-4 text-center text-[11px] text-ink-300">
        Scan · View · Book — no app required
      </footer>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} qrToken={qrToken} />
    </div>
  );
}
